import {
  ChangeEvent,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useState
} from 'react';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import { UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { AccessElements, StatusCode } from 'helpers/app.config';
import CustomFormChangeStatusModal from 'components/shared/CustomFormChangeStatusModal';
import TextAreaElement from 'components/muiInputWrappers/TextAreaElement';
import { AvailableStatus, TicketDetail } from 'api/ticket';
import { FormItem, FormTitle } from 'components/shared';
import { AutocompleteElement } from 'components/muiInputWrappers';
import { useGlobalContext } from 'context/GlobalContext';
import useGetGroupsEngineersOnly from 'hooks/security/useGetGroupsEngineersOnly';

import TicketDetails from '../AcceptRejectModalDialog/components/TicketDetails';
import useGeneralChangeStatusServices from './useGeneralChangeStatusServices';

interface Props {
  acceptButtonText?: string;
  children?: ReactNode;
  commentTitle?: string;
  destinyStatus: AvailableStatus;
  hideAcceptButton?: boolean;
  isMutating?: boolean;
  isObligatory?: boolean;
  isOpen: boolean;
  isTicketStatusToClosed?: boolean;
  notificationMaxLength?: number;
  onCancel: () => void;
  onSubmit: (form: UseFormReturn<FormFieldsChangeStatus>) => void;
  ticketDetails?: TicketDetail;
  ticketId: string;
  title?: string;
}

export interface FormFieldsChangeStatus {
  comment: string;
  taskForce?: string;
  remote?: boolean;
  groupId?: number;
}
const SERVICE_TYPE_INCIDENT = { id: 9, name: 'incidente' };
const SERVICE_TYPE_LAUNCH = { id: 32, name: 'lanzamiento' };

function GeneralModalChangeStatus({
  acceptButtonText,
  children,
  commentTitle,
  destinyStatus,
  hideAcceptButton = false,
  isMutating,
  isObligatory = false,
  isOpen,
  isTicketStatusToClosed = true,
  notificationMaxLength,
  onCancel,
  onSubmit,
  ticketDetails = null,
  ticketId,
  title
}: Props) {
  const { hasPerm } = useGlobalContext();
  const form = useForm<FormFieldsChangeStatus>({
    mode: 'onBlur'
  });
  const [needTaskForces, setNeedTaskForces] = useState(false);
  const [redColor, setRedColor] = useState(false);
  const [isRemoteChecked, setRemoteChecked] = useState(false);
  const [projectName, setProjectName] = useState('');

  const { getTicketDetail, getTaskForces } = useGeneralChangeStatusServices({
    ticketId,
    enabled: !ticketDetails,
    taskForces: needTaskForces
  });

  const taskForceId = form.watch('taskForce');
  const remoteWatched = form.watch('remote');
  const groupId = form.watch('groupId');

  const { t } = useTranslation('global');

  const isRemoteAvailable = useMemo(
    () =>
      getTicketDetail?.data?.ticketServiceTypeName ===
        SERVICE_TYPE_INCIDENT.name ||
      getTicketDetail?.data?.ticketServiceTypeId === SERVICE_TYPE_INCIDENT.id ||
      getTicketDetail?.data?.ticketServiceTypeId === SERVICE_TYPE_LAUNCH.id ||
      getTicketDetail?.data?.ticketServiceTypeName === SERVICE_TYPE_LAUNCH.name,
    [getTicketDetail?.data]
  );

  const isIncident = useMemo(
    () =>
      getTicketDetail?.data?.ticketServiceTypeId === SERVICE_TYPE_INCIDENT.id,
    [getTicketDetail?.data]
  );

  const handleRemoteChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      form.setValue('remote', event?.target?.checked);
      setRemoteChecked(event?.target?.checked);
    },
    [form]
  );

  const handleCloseModal = useCallback(() => {
    onCancel();
    form.reset();
    setRemoteChecked(false);
    setProjectName('');
  }, [onCancel, form]);

  useEffect(() => {
    if (getTicketDetail?.data) {
      setProjectName(getTicketDetail?.data?.ticketProjectName);
      if (
        getTicketDetail?.data?.ticketServiceTypeId === SERVICE_TYPE_LAUNCH.id ||
        getTicketDetail?.data?.ticketServiceTypeName ===
          SERVICE_TYPE_LAUNCH.name
      ) {
        handleRemoteChange({
          target: { checked: true }
        } as ChangeEvent<HTMLInputElement>);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getTicketDetail?.data]);

  useEffect(() => {
    setRedColor(
      getTaskForces?.data?.find(
        (taskForceFiltered) => taskForceFiltered.userId === +taskForceId
      )?.accessTypeId === 2
    );
  }, [taskForceId, getTaskForces?.data]);

  useEffect(() => {
    if (destinyStatus) {
      setNeedTaskForces(destinyStatus?.id === StatusCode.PendingConfirmation);
    }
  }, [destinyStatus]);

  const handleSubmit = async () => {
    if (getTicketDetail?.data && !Object.keys(getTicketDetail?.data).length)
      return;
    await form.handleSubmit(() => onSubmit(form))();
  };

  const currentStatus = getTicketDetail?.data?.ticketStatusName || '';
  const destinyStatusName = destinyStatus?.name;

  const message = useMemo(() => {
    if (destinyStatus?.id === StatusCode.Scheduled) {
      // Agendado
      return t('ticket.changeStatus.rejectScheduled');
    }
    if (
      destinyStatus?.id !== StatusCode.Closed ||
      (destinyStatus?.id === StatusCode.Closed &&
        getTicketDetail?.data?.ticketStatusId === StatusCode.Historic)
    ) {
      return t('ticket.changeStatus.updateTicketMessage', {
        ticketId,
        currentStatus,
        destinyStatusName
      });
    }

    return t('ticket.changeStatus.finishTicketMessage', {
      ticketId
    });
  }, [
    currentStatus,
    destinyStatus,
    destinyStatusName,
    getTicketDetail?.data?.ticketStatusId,
    t,
    ticketId
  ]);

  const { groupsEngineersOnly, isGroupsEngineersOnlyLoading } =
    useGetGroupsEngineersOnly({
      isEnabled:
        destinyStatus?.id === StatusCode.PendingConfirmation &&
        !!getTicketDetail?.data?.ticketClientId,
      clientId: getTicketDetail?.data?.ticketClientId
    });

  const engineersOnly = useMemo(() => {
    if (groupId) {
      const groupEngineersOnly = groupsEngineersOnly?.find(
        (group) => group?.id === groupId
      );
      if (groupEngineersOnly) return groupEngineersOnly?.engineers;
      return [];
    }
    return getTaskForces?.data?.map((taskForce) => ({
      id: taskForce?.userId,
      label: taskForce?.fullName,
      accessTypeId: taskForce?.accessTypeId
    }));
  }, [getTaskForces?.data, groupId, groupsEngineersOnly]);

  return (
    <CustomFormChangeStatusModal
      acceptButtonText={acceptButtonText}
      bodyContent={
        getTicketDetail.isLoading ? (
          <Grid justifyContent="center" container>
            <Grid item>
              <CircularProgress size={30} />
            </Grid>
          </Grid>
        ) : (
          <TicketDetails
            dialogData={{
              dataTickets: [ticketId],
              open: isOpen
            }}
            ticketDetail={ticketDetails || getTicketDetail?.data}
          />
        )
      }
      customForm={
        <form onSubmit={form.handleSubmit(() => handleSubmit())}>
          {children}
          {destinyStatus?.id === StatusCode.PendingConfirmation && (
            <Grid lg={12} marginBottom="16px" md={12} sm={12} xs={12} item>
              {isIncident && (
                <>
                  <FormTitle>{t('users.group')}</FormTitle>
                  <FormItem>
                    <AutocompleteElement
                      autocompleteProps={{
                        size: 'small'
                      }}
                      control={form.control}
                      loading={isGroupsEngineersOnlyLoading}
                      name="groupId"
                      options={groupsEngineersOnly}
                      matchId
                    />
                  </FormItem>
                </>
              )}
              <FormTitle variant="body2" gutterBottom>
                {t('ticket.changeStatus.assignTo')}:
              </FormTitle>
              {!remoteWatched ? (
                <FormItem>
                  <AutocompleteElement
                    autocompleteProps={{
                      size: 'small'
                    }}
                    control={form.control}
                    loading={getTaskForces?.isLoading}
                    name="taskForce"
                    options={engineersOnly || []}
                    textFieldProps={
                      redColor
                        ? {
                            sx: { input: { color: 'red' } }
                          }
                        : null
                    }
                    matchId
                    required
                  />
                </FormItem>
              ) : (
                <TextField
                  data-testid="work-force-remote"
                  size="small"
                  sx={{ width: '100%', backgroundColor: 'white' }}
                  value={`${t(
                    'ticket.changeStatus.remoteDiagnostic'
                  )} - ${projectName}`}
                  disabled
                  required
                />
              )}
              {hasPerm([AccessElements.TicketRemoteAssignation]) &&
                destinyStatus?.id === StatusCode.PendingConfirmation &&
                isRemoteAvailable && (
                  <Grid data-testid="remote-checkbox">
                    <FormControlLabel
                      checked={isRemoteChecked}
                      control={
                        <Checkbox name="remote" onChange={handleRemoteChange} />
                      }
                      disabled={
                        getTicketDetail?.data?.ticketServiceTypeId ===
                          SERVICE_TYPE_LAUNCH.id ||
                        getTicketDetail?.data?.ticketServiceTypeName ===
                          SERVICE_TYPE_LAUNCH.name
                      }
                      label={t('common.commonProperties.remoteAttention')}
                      name="remote"
                    />
                  </Grid>
                )}
            </Grid>
          )}
          {isTicketStatusToClosed && (
            <div
              style={{
                marginBottom: '1rem',
                marginTop: '1rem'
              }}
            >
              <TextAreaElement
                form={form}
                maxLength={notificationMaxLength}
                name="comment"
                title={
                  commentTitle ||
                  (isObligatory
                    ? `* ${t('common.commonProperties.comments')}`
                    : t('common.commonProperties.comment'))
                }
                validation={{
                  required: {
                    value: isObligatory,
                    message: t('common.commonMessages.requiredField')
                  }
                }}
              />
            </div>
          )}
        </form>
      }
      destinyStatus={destinyStatus}
      dialogProps={{
        open: isOpen,
        title: title || t('ticket.changeStatus.updatingStatus')
      }}
      headerContent={message}
      hideAcceptButton={hideAcceptButton}
      isMutating={getTicketDetail.isLoading || isMutating}
      onCancelClickButton={handleCloseModal}
      originStatus={getTicketDetail?.data?.ticketStatusName}
      ticketIds={[ticketId]}
    />
  );
}

export default GeneralModalChangeStatus;
